import React, { useState } from 'react';
/**
 * `MobileInfoBar` renders an accordion section for mobile that surfaces helpful
 * information and documents alongside the main journey pages.
 *
 * Behaviour:
 * - Panel heading and content vary by `currentPath` (vehicle/products/create
 *   plan vs checkout-related pages).
 * - Renders page-specific info components and `UsefulDocuments`.
 * - Respects CMS flags such as `common.hideCustomerPlan`.
 */

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Journey, PageAbsolutePath } from '../../consts/Common';
import UsefulDocuments from '../sitePages/UsefulDocuments';
import PageInfoPlanSummary from '../sitePages/PageInfoPlanSummary';
import PageInfoProduct from '../sitePages/PageInfoProduct';
import PageInfoCreatePlan from '../sitePages/PageInfoCreatePlan';
import PageInfoVehicle from '../sitePages/PageInfoVehicle';
import { DocumentView } from './Constants';
import { DocumentTreeItem, ProductType } from './CommonTypes';
import { useAppSelector } from '../../app-state/Hooks';

type PageInfoProps = {
  documents: DocumentTreeItem[];
  expandedDocIds: string[];
  products: ProductType[];
  currentPath: string;
  setExpandedDocIds: (docIds: string[]) => void;
};

const MobileInfoBar = ({
  documents,
  expandedDocIds,
  products,
  currentPath,
  setExpandedDocIds
}: PageInfoProps): JSX.Element => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const cms = useAppSelector((s) => s.content);
  const appConfig = useAppSelector((s) => s.appConfig);
  const userId = useAppSelector((s) => s.user?.userId);

  const isCCWUser: boolean =
    appConfig.journeyType === Journey.Customer && !!userId;

  const handleChange = () => setExpanded(!expanded);

  const getPanelHeading = () => {
    switch (currentPath) {
      case PageAbsolutePath.VehicleDetails:
        return 'More information';
      case PageAbsolutePath.Products:
        return 'More information';
      case PageAbsolutePath.CreatePlan:
        return 'More information';
      case PageAbsolutePath.PersonalDetails:
      case PageAbsolutePath.Summary:
      case PageAbsolutePath.DirectDebit:
      case PageAbsolutePath.SinglePayment:
        return 'Customer plan';
      default:
        return '';
    }
  };

  const getAccordionContent = () => {
    switch (currentPath) {
      case PageAbsolutePath.VehicleDetails:
      case PageAbsolutePath.Customer.VehicleDetails:
        return (
          <>
            <PageInfoVehicle showReasonsToProtect={!isCCWUser} />
            <UsefulDocuments
              documents={documents}
              cmsDocuments={cms.usefulDocuments}
              view={DocumentView.Mobile}
              expandedDocIds={expandedDocIds}
              setExpandedDocIds={setExpandedDocIds}
              showDivider={true}
            />
          </>
        );
      case PageAbsolutePath.Products:
      case PageAbsolutePath.Customer.Products:
        return (
          <>
            <PageInfoProduct showReasonsToProtect={!isCCWUser} />
            <UsefulDocuments
              documents={documents}
              cmsDocuments={cms.usefulDocuments}
              view={DocumentView.Mobile}
              expandedDocIds={expandedDocIds}
              setExpandedDocIds={setExpandedDocIds}
              showDivider={true}
            />
          </>
        );
      case PageAbsolutePath.CreatePlan:
      case PageAbsolutePath.Customer.CreatePlan:
        return (
          <>
            <PageInfoCreatePlan showReasonsToProtect={!isCCWUser} />
            <UsefulDocuments
              documents={documents}
              cmsDocuments={cms.usefulDocuments}
              view={DocumentView.Mobile}
              expandedDocIds={expandedDocIds}
              setExpandedDocIds={setExpandedDocIds}
              showDivider={true}
            />
          </>
        );
      case PageAbsolutePath.PersonalDetails:
      case PageAbsolutePath.Summary:
      case PageAbsolutePath.DirectDebit:
      case PageAbsolutePath.SinglePayment:
      case PageAbsolutePath.Customer.PersonalDetails:
      case PageAbsolutePath.Customer.Summary:
      case PageAbsolutePath.Customer.DirectDebit:
      case PageAbsolutePath.Customer.SinglePayment:
        return (
          <>
            {cms.common?.hideCustomerPlan !== true && (
              <PageInfoPlanSummary
                products={products}
                culture={appConfig.cultureCode}
              />
            )}
            <UsefulDocuments
              documents={documents}
              cmsDocuments={cms.usefulDocuments}
              view={DocumentView.Mobile}
              expandedDocIds={expandedDocIds}
              setExpandedDocIds={setExpandedDocIds}
              showDivider={false}
            />
          </>
        );
      default:
        return '';
    }
  };

  const getContentClass = () => {
    let contentClass = 'mobile-content-info page-info-container';
    if (
      currentPath === PageAbsolutePath.PersonalDetails ||
      currentPath === PageAbsolutePath.Summary ||
      currentPath === PageAbsolutePath.DirectDebit ||
      currentPath === PageAbsolutePath.SinglePayment
    ) {
      contentClass = 'mobile-content-info';
    }
    return contentClass;
  };

  return (
    <div>
      <div className='desktop-hide mobile-info-bar'>
        <Accordion expanded={expanded} onChange={handleChange}>
          <AccordionSummary
            id='mobile-more-information'
            expandIcon={
              <span className='float-right'>
                <ExpandLessIcon />
              </span>
            }>
            <span>{getPanelHeading()}</span>
          </AccordionSummary>
          <AccordionDetails>
            {<div className={getContentClass()}>{getAccordionContent()}</div>}
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
};

export default MobileInfoBar;
